import { useState, useEffect } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { useServerPagination } from '@/hooks/useServerPagination';
import { Search, ArrowLeftRight, TrendingUp, TrendingDown, Plus, ChevronRight, FileSpreadsheet, Clock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import AddTransactionSheet from '@/components/transactions/AddTransactionSheet';
import ImportTransactionsSheet from '@/components/transactions/ImportTransactionsSheet';
import { cn } from '@/lib/utils';
import { formatBusinessDate, formatBusinessTime } from '@/lib/format';

type TransactionType = 'CREDIT' | 'DEBIT' | 'all';
type CategoryFilter = keyof typeof PURPOSE_LABELS | 'all' | 'unassigned';

const PURPOSE_LABELS: Record<string, { label: string; color: string }> = {
    repayment:                { label: 'Repayment',         color: 'bg-blue-50 text-blue-700 border-blue-200' },
    vehicle_procurement:      { label: 'Procurement',       color: 'bg-purple-50 text-purple-700 border-purple-200' },
    vehicle_sale:             { label: 'Vehicle Sale',      color: 'bg-green-50 text-green-700 border-green-200' },
    deposit:                  { label: 'Deposit',           color: 'bg-amber-50 text-amber-700 border-amber-200' },
    deposit_refund:           { label: 'Deposit Refund',    color: 'bg-purple-50 text-purple-700 border-purple-200' },
    expense:                  { label: 'Expense',           color: 'bg-orange-50 text-orange-700 border-orange-200' },
    partner_payout:           { label: 'Payout',            color: 'bg-pink-50 text-pink-700 border-pink-200' },
    maintenance:              { label: 'Maintenance',       color: 'bg-teal-50 text-teal-700 border-teal-200' },
    registration:             { label: 'Registration',      color: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
    loan:                     { label: 'Advance',           color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    loan_repayment:           { label: 'Advance Repayment', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    insurance_premium:        { label: 'Insurance',         color: 'bg-sky-50 text-sky-700 border-sky-200' },
    insurance_claim:          { label: 'Ins. Claim',        color: 'bg-sky-50 text-sky-700 border-sky-200' },
    insurance_loan_deposit:   { label: 'Ins. Advance Dep.', color: 'bg-sky-50 text-sky-700 border-sky-200' },
    insurance_loan_repayment: { label: 'Ins. Advance Rep.', color: 'bg-sky-50 text-sky-700 border-sky-200' },
    premium_discount:         { label: 'Premium Disc.',     color: 'bg-lime-50 text-lime-700 border-lime-200' },
    reversal:                 { label: 'Reversal',          color: 'bg-slate-50 text-slate-700 border-slate-200' },
};

const EXPENSE_TYPE_LABELS: Record<string, string> = {
    internet:        'Internet',
    fuel:            'Fuel',
    salary:          'Salary',
    auto_parts:      'Auto Parts',
    family:          'Family',
    refund:          'Refund',
    roadworthiness:  'Roadworthy',
    office:          'Office',
    other:           'Other',
    referral:        'Referral',
    tracker:         'Tracker',
    transportation:  'Transport',
};

export default function Transactions() {
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState<TransactionType>('all');
    const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
    const [sheetOpen, setSheetOpen] = useState(false);
    const [importSheetOpen, setImportSheetOpen] = useState(false);
    const [selectedTransactionId, setSelectedTransactionId] = useState<number | null>(null);
    const [missingTime, setMissingTime] = useState(false);
    const debouncedSearch = useDebounce(searchTerm, 300);

    const { data: countData } = useQuery({
        queryKey: ['transactions-count', debouncedSearch, typeFilter, categoryFilter, missingTime],
        queryFn: async () => {
            if (missingTime) {
                const { data, error } = await supabase.rpc('count_transactions_missing_time');
                if (error) throw error;
                return Number(data) || 0;
            }
            if (categoryFilter !== 'all') {
                const { data, error } = await supabase.rpc('count_transactions_by_purpose', {
                    purpose_filter: categoryFilter,
                    search_query: debouncedSearch || null,
                    type_filter: typeFilter !== 'all' ? typeFilter : null,
                });
                if (error) throw error;
                return Number(data) || 0;
            }
            let query = supabase.from('transactions').select('*', { count: 'exact', head: true });
            if (debouncedSearch) {
                query = query.ilike('description', `%${debouncedSearch}%`);
            }
            if (typeFilter !== 'all') {
                query = query.eq('type', typeFilter);
            }
            const { count, error } = await query;
            if (error) throw error;
            return count || 0;
        },
    });

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage, itemsPerPage } = useServerPagination({
        totalCount: countData || 0,
        itemsPerPage: 20,
    });

    useEffect(() => {
        resetPage();
    }, [debouncedSearch, typeFilter, categoryFilter, missingTime]);

    const { data: transactions, isLoading } = useQuery({
        queryKey: ['transactions', currentPage, itemsPerPage, debouncedSearch, typeFilter, categoryFilter, missingTime],
        queryFn: async () => {
            if (missingTime) {
                const { data, error } = await supabase.rpc('get_transactions_missing_time', {
                    p_limit: itemsPerPage,
                    p_offset: from,
                });
                if (error) throw error;
                return data;
            }
            if (categoryFilter !== 'all') {
                const { data, error } = await supabase.rpc('get_transactions_by_purpose', {
                    purpose_filter: categoryFilter,
                    search_query: debouncedSearch || null,
                    type_filter: typeFilter !== 'all' ? typeFilter : null,
                    p_limit: itemsPerPage,
                    p_offset: from,
                });
                if (error) throw error;
                return data;
            }
            let query = supabase
                .from('transactions')
                .select('*')
                .order('transaction_at', { ascending: false });
            if (debouncedSearch) {
                query = query.ilike('description', `%${debouncedSearch}%`);
            }
            if (typeFilter !== 'all') {
                query = query.eq('type', typeFilter);
            }
            query = query.range(from, to);
            const { data, error } = await query;
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined,
    });

    // Fetch purposes for the current page of transactions
    const transactionIds = transactions?.map((t) => t.id) ?? [];
    const { data: purposeMap } = useQuery({
        queryKey: ['transaction-purposes', transactionIds],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_transaction_purposes', {
                transaction_ids: transactionIds,
            });
            if (error) throw error;
            const map = new Map<number, { purpose: string; subPurpose: string | null }>();
            for (const row of data || []) {
                map.set(row.transaction_id, { purpose: row.purpose, subPurpose: row.sub_purpose ?? null });
            }
            return map;
        },
        enabled: transactionIds.length > 0,
    });

    const formatCurrency = (amount: number, currency: string = 'GHS') => {
        return new Intl.NumberFormat('en-GH', { style: 'currency', currency }).format(amount);
    };

    const formatDate = (dateStr: string) => ({
        date: formatBusinessDate(dateStr),
        time: formatBusinessTime(dateStr),
    });

    const handleAddTransaction = () => {
        setSelectedTransactionId(null);
        setSheetOpen(true);
    };

    const handleEditTransaction = (tx: any) => {
        setSelectedTransactionId(tx.id);
        setSheetOpen(true);
    };

    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6 pb-12">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <ArrowLeftRight className="h-7 w-7 text-muted-foreground" />
                        <h1 className="text-3xl font-semibold tracking-tight">Transactions</h1>
                    </div>
                    <p className="text-sm text-muted-foreground ml-10">Central financial ledger — all credits and debits.</p>
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="h-9">
                            <Plus className="h-4 w-4 mr-2" />
                            New
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={handleAddTransaction}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add Transaction
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setImportSheetOpen(true)}>
                            <FileSpreadsheet className="h-4 w-4 mr-2" />
                            Import Statement
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Filters */}
            <Card className="border-0">
                <CardHeader>
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search transactions..."
                                className="pl-9"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as TransactionType)}>
                            <SelectTrigger className="w-32">
                                <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Types</SelectItem>
                                <SelectItem value="CREDIT">Credits</SelectItem>
                                <SelectItem value="DEBIT">Debits</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as CategoryFilter)}>
                            <SelectTrigger className="w-40">
                                <SelectValue placeholder="Category" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Categories</SelectItem>
                                <SelectItem value="unassigned">Unassigned</SelectItem>
                                {Object.entries(PURPOSE_LABELS)
                                    .sort(([, a], [, b]) => a.label.localeCompare(b.label))
                                    .map(([key, { label }]) => (
                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            variant={missingTime ? 'default' : 'outline'}
                            size="sm"
                            className="h-9 text-xs"
                            onClick={() => setMissingTime(!missingTime)}
                        >
                            <Clock className="h-3.5 w-3.5 mr-1.5" />
                            Missing Time
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>

            {/* Pagination */}
                <ListPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={goToPage}
                    totalItems={totalItems}
                />

            {/* Transaction Table */}
            {isLoading || !transactions ? (
                <div className="space-y-3">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <Skeleton key={i} className="h-12 w-full rounded-xl" />
                    ))}
                </div>
            ) : transactions && transactions.length > 0 ? (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-12"></TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead className="w-8"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {transactions.map((tx) => {
                            const isDebit = tx.type === 'DEBIT';
                            const { date, time } = tx.transaction_at
                                ? formatDate(tx.transaction_at)
                                : { date: '', time: '' };
                            const entry = purposeMap?.get(tx.id);
                            const purposeInfo = entry ? PURPOSE_LABELS[entry.purpose] : null;
                            const subPurposeLabel = entry?.subPurpose ? EXPENSE_TYPE_LABELS[entry.subPurpose] : null;

                            return (
                                <TableRow
                                    key={tx.id}
                                    onClick={() => handleEditTransaction(tx)}
                                    className="cursor-pointer group"
                                >
                                    {/* Type icon */}
                                    <TableCell>
                                        <div className={cn(
                                            'flex items-center justify-center h-8 w-8 rounded-full',
                                            isDebit
                                                ? 'bg-red-100 text-red-600'
                                                : 'bg-emerald-100 text-emerald-600'
                                        )}>
                                            {isDebit ? (
                                                <TrendingDown className="h-3.5 w-3.5" />
                                            ) : (
                                                <TrendingUp className="h-3.5 w-3.5" />
                                            )}
                                        </div>
                                    </TableCell>

                                    {/* Description */}
                                    <TableCell>
                                        <p className="text-sm font-medium leading-snug max-w-md break-words">
                                            {tx.description || 'No description'}
                                        </p>
                                    </TableCell>

                                    {/* Date */}
                                    <TableCell>
                                        <div className="whitespace-nowrap">
                                            <p className="text-sm text-muted-foreground">{date}</p>
                                            {time && (
                                                <p className="text-xs text-muted-foreground/50">{time}</p>
                                            )}
                                        </div>
                                    </TableCell>

                                    {/* Category */}
                                    <TableCell>
                                        <div className="flex items-center gap-1 flex-wrap">
                                            {purposeInfo ? (
                                                <Badge
                                                    variant="outline"
                                                    className={cn('text-[10px] h-5 px-1.5 font-normal', purposeInfo.color)}
                                                >
                                                    {purposeInfo.label}
                                                </Badge>
                                            ) : null}
                                            {subPurposeLabel ? (
                                                <Badge
                                                    variant="outline"
                                                    className="text-[10px] h-5 px-1.5 font-normal bg-orange-50/50 text-orange-600 border-orange-200/60"
                                                >
                                                    {subPurposeLabel}
                                                </Badge>
                                            ) : null}
                                        </div>
                                    </TableCell>

                                    {/* Amount */}
                                    <TableCell className="text-right">
                                        <span className={cn(
                                            'text-sm font-semibold tabular-nums',
                                            isDebit ? 'text-red-600' : 'text-emerald-600'
                                        )}>
                                            {isDebit ? '-' : '+'}{formatCurrency(Math.abs(tx.amount), tx.currency || 'GHS')}
                                        </span>
                                        {tx.fee ? (
                                            <p className="text-[11px] text-orange-500 tabular-nums">
                                                Fee: {formatCurrency(tx.fee, tx.currency || 'GHS')}
                                            </p>
                                        ) : null}
                                    </TableCell>

                                    {/* Chevron */}
                                    <TableCell>
                                        <ChevronRight className="h-4 w-4 text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors" />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <ArrowLeftRight className="h-10 w-10 text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground text-sm">No transactions found</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Try adjusting your search or filters.</p>
                </div>
            )}

                </CardContent>
            </Card>

            <AddTransactionSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                transactionId={selectedTransactionId}
            />

            <ImportTransactionsSheet
                open={importSheetOpen}
                onOpenChange={setImportSheetOpen}
            />
        </div>
    );
}
